(() => {
  class FeaturedMenuCoordinator {
    constructor(closeDelay = 160) {
      this.closeDelay = closeDelay;
      this.activeItem = null;
      this.hoveredItem = null;
      this.closeTimer = null;
    }

    open(item) {
      this.cancelClose();
      if (this.activeItem && this.activeItem !== item) this.activeItem.setExpanded(false);
      this.activeItem = item;
      item.setExpanded(true);
    }

    close(item = this.activeItem) {
      this.cancelClose();
      if (!item || this.activeItem !== item) return;
      item.setExpanded(false);
      this.activeItem = null;
    }

    scheduleClose(item, handoffHovered = false) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        this.close(item);
        if (handoffHovered && this.hoveredItem?.pointerInside) this.open(this.hoveredItem);
      }, this.closeDelay);
    }

    setHovered(item) {
      this.hoveredItem = item;
    }

    clearHovered(item) {
      if (this.hoveredItem === item) this.hoveredItem = null;
    }

    cancelClose() {
      if (this.closeTimer === null) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  globalThis.FeaturedMenuCoordinator = FeaturedMenuCoordinator;

  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;

  const coordinator = new FeaturedMenuCoordinator();
  document.addEventListener('catalog-menu:open', () => coordinator.close());

  class FeaturedMenu extends HTMLElement {
    constructor() {
      super();
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.trigger = this.querySelector('[data-featured-trigger]');
      this.panel = this.querySelector('[data-featured-panel]');
      if (!this.trigger || !this.panel) return;

      this.pointerInside = false;
      this.focusInside = false;

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      this.setAttribute('data-ready', '');
      this.isInitialized = true;
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      coordinator.clearHovered(this);
      if (coordinator.activeItem === this) coordinator.close(this);
      this.isInitialized = false;
    }

    setExpanded(expanded) {
      if (expanded) {
        document
          .querySelectorAll('.header-catalog-menu__details[open]')
          .forEach((details) => (details.open = false));
      }
      this.toggleAttribute('data-open', expanded);
      this.trigger.setAttribute('aria-expanded', String(expanded));
      this.panel.hidden = !expanded;
    }

    onPointerEnter() {
      this.pointerInside = true;
      coordinator.setHovered(this);
      if (coordinator.activeItem?.focusInside && coordinator.activeItem !== this) return;
      coordinator.open(this);
    }

    onPointerLeave() {
      this.pointerInside = false;
      coordinator.clearHovered(this);
      this.scheduleCloseIfInactive();
    }

    onFocusIn() {
      this.focusInside = true;
      coordinator.open(this);
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) {
        this.focusInside = true;
        coordinator.cancelClose();
        return;
      }
      this.focusInside = false;
      this.scheduleCloseIfInactive(true);
    }

    scheduleCloseIfInactive(handoffHovered = false) {
      if (coordinator.activeItem !== this) return;
      if (this.pointerInside || this.focusInside) {
        coordinator.cancelClose();
        return;
      }
      coordinator.scheduleClose(this, handoffHovered);
    }

    onKeyDown(event) {
      if (event.key !== 'Escape' || coordinator.activeItem !== this) return;
      event.preventDefault();
      this.trigger.focus();
      coordinator.close(this);
    }

    onDocumentPointerDown(event) {
      if (coordinator.activeItem === this && !this.contains(event.target)) coordinator.close(this);
    }
  }

  if (!customElements.get('featured-menu')) customElements.define('featured-menu', FeaturedMenu);
})();
